import React from 'react';
import Select from '../../Components/Select';
import Expandable from '../../Components/Expandable';
import { Camera, PlayCircle, Trash2, Zap } from '../../Components/Icon';
import IconButton from '../../Components/IconButton';
import { BuilderActionType, BuilderContext } from './state';
import PageData, { PageEvent, PageEventTrigger } from '../../Model/PageData';
import VendexAction from '../../Model/VendexAction';
import {
    BuilderMetaPill,
    BuilderPanel,
    BuilderPillButton,
} from './BuilderPanel';
import ActionSelector from './ActionSelector';

const getDefaultEvent = (trigger: PageEventTrigger): PageEvent => {
    return {
        trigger,
        actions: [],
    };
};

const sanitizeActions = (actions: VendexAction[]) => {
    return actions.map(({ trigger, ...action }) => action);
};

const getEventTitle = (event: PageEvent) => {
    if (event.trigger === 'onLoad') {
        return 'On load';
    }

    if (event.trigger === 'onQrScan') {
        return 'QR scan';
    }
    return '';
};

const getEventIcon = (trigger: PageEventTrigger) => {
    switch (trigger) {
        case 'onLoad':
            return <PlayCircle className="h-4 w-4" />;
        case 'onQrScan':
            return <Camera className="h-4 w-4" />;
        default:
            return <Zap className="h-4 w-4" />;
    }
};

function PageEventsEditor() {
    const { previewPage } = React.useContext(BuilderContext).state;
    const { dispatch } = React.useContext(BuilderContext);
    const [openEvent, setOpenEvent] = React.useState<string>('');

    React.useEffect(() => {
        setOpenEvent('');
    }, [previewPage?.id]);

    if (!previewPage) {
        return null;
    }

    const updatePageEvents = (events: PageEvent[]) => {
        const nextPage: PageData = {
            ...previewPage,
            events: events.length > 0 ? events : undefined,
        };
        dispatch({
            type: BuilderActionType.UPDATE_PREVIEW_PAGE,
            payload: nextPage,
        });
    };

    const events = previewPage.events ?? [];

    const handleAddEvent = (trigger: PageEventTrigger) => {
        const nextEvents = [...events, getDefaultEvent(trigger)];
        updatePageEvents(nextEvents);
        setOpenEvent(`${nextEvents.length - 1}`);
    };

    const handleEventChange = (index: number, value: PageEvent) => {
        const nextEvents = [...events];
        nextEvents[index] = value;
        updatePageEvents(nextEvents);
    };

    const handleTriggerChange = (index: number, trigger: PageEventTrigger) => {
        const currentEvent = events[index];
        const nextEvent = getDefaultEvent(trigger);
        nextEvent.actions = sanitizeActions(currentEvent.actions);
        handleEventChange(index, nextEvent);
    };

    const handleDelete = (index: number) => {
        const nextEvents = [...events];
        nextEvents.splice(index, 1);
        updatePageEvents(nextEvents);
    };

    return (
        <BuilderPanel
            title="Page events"
            icon={<Zap className="h-4 w-4" />}
            description="Attach runtime actions to page entry or QR scans."
            className="mt-4"
            actions={
                <>
                    <BuilderMetaPill>
                        {events.length} event{events.length === 1 ? '' : 's'}
                    </BuilderMetaPill>
                    <BuilderPillButton
                        tone="accent"
                        onClick={() => handleAddEvent('onLoad')}
                        icon={<PlayCircle className="h-4 w-4" />}
                    >
                        On load
                    </BuilderPillButton>
                    <BuilderPillButton
                        tone="accent"
                        onClick={() => handleAddEvent('onQrScan')}
                        icon={<Camera className="h-4 w-4" />}
                    >
                        QR scan
                    </BuilderPillButton>
                </>
            }
            bodyClassName="space-y-4"
        >
            {events.map((event, index) => (
                <Expandable
                    key={`${event.trigger}-${index.toString()}`}
                    open={openEvent === `${index}`}
                    onToggle={() =>
                        setOpenEvent(openEvent === `${index}` ? '' : `${index}`)
                    }
                    header={
                        <div className="flex items-center gap-3 pr-10">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                                {getEventIcon(event.trigger)}
                            </span>
                            <div className="min-w-0">
                                <p className="font-semibold text-slate-900">
                                    {getEventTitle(event)}
                                </p>
                                <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                                    <BuilderMetaPill className="bg-slate-100 text-slate-500">
                                        {event.actions.length} action
                                        {event.actions.length === 1 ? '' : 's'}
                                    </BuilderMetaPill>
                                </div>
                            </div>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(index);
                                }}
                                className="absolute right-12 top-3 text-red-600"
                            >
                                <Trash2 />
                            </IconButton>
                        </div>
                    }
                >
                    <div className="grid gap-3 md:grid-cols-2">
                        <Select
                            label="Trigger"
                            value={event.trigger}
                            onChange={(e) =>
                                handleTriggerChange(
                                    index,
                                    e.target.value as PageEventTrigger,
                                )
                            }
                        >
                            <option value="onLoad">onLoad</option>
                            <option value="onQrScan">onQrScan</option>
                        </Select>
                    </div>
                    <ActionSelector
                        actions={event.actions}
                        onChange={(actions: VendexAction[]) =>
                            handleEventChange(index, {
                                ...event,
                                actions: sanitizeActions(actions),
                            })
                        }
                        showTriggerSelection={false}
                    />
                </Expandable>
            ))}
            {events.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                    No page-level events configured yet. Start with an onLoad
                    event for page entry behavior or a QR scan event for
                    scanned interactions.
                </div>
            )}
        </BuilderPanel>
    );
}

export default PageEventsEditor;
